/**
 * One-Pager: Scroll-To Navigation, aktiver Link-State & Offer-URL-Routing
 *
 * URL-Schema:   ?beratungsangebot=<slug>
 *   - Direktaufruf öffnet automatisch das passende Offer-Overlay.
 *   - Beim Öffnen / Schliessen wird der Param via History API gesetzt / entfernt.
 *
 * PHP-Konfiguration via window.OFFER_ROUTING:
 *   { paramName: 'beratungsangebot', initialSlug: 'verhuetung' | null }
 */

// ── Konfiguration ────────────────────────────────────────────────────────────

const config = () => window.OFFER_ROUTING || { paramName: 'beratungsangebot', initialSlug: null };

// ── URL-Hilfsfunktionen ──────────────────────────────────────────────────────

/** Setzt oder entfernt den ?beratungsangebot=<slug> Param ohne Reload. */
function setOfferUrl(slug) {
  const param = config().paramName;
  const url   = new URL(window.location.href);

  if (slug) {
    url.searchParams.set(param, slug);
  } else {
    url.searchParams.delete(param);
  }

  // Query komplett weglassen wenn leer → URL bleibt sauber
  const search = url.searchParams.toString();
  const newUrl = url.pathname + (search ? '?' + search : '') + url.hash;

  history.pushState({ offerSlug: slug ?? null }, '', newUrl);
}

/** Liest aktuellen Slug aus der URL */
function getOfferSlugFromUrl() {
  return new URL(window.location.href).searchParams.get(config().paramName);
}

/** Öffnet ein Overlay programmatisch über den vorhandenen open-Button */
function triggerOverlayOpen(overlayKey) {
  const btn = document.querySelector(`[data-overlay-open="${overlayKey}"]`);
  btn?.click();
}

/** Schliesst das aktuell offene Overlay über seinen close-Button */
function triggerOverlayClose(overlayKey) {
  const btn = document.querySelector(
    `[data-overlay="${overlayKey}"] [data-overlay-close]`
  );
  btn?.click();
}

// ── URL ↔ Overlay synchronisieren ───────────────────────────────────────────

function initOfferUrlRouting() {
  // Overlay öffnet sich → URL-Param setzen
  window.addEventListener('overlay:opened', e => {
    const { key } = e.detail;
    if (!key.startsWith('offer-')) return;
    setOfferUrl(key.replace('offer-', ''));
  });

  // Overlay schliesst sich → URL-Param entfernen
  window.addEventListener('overlay:closed', e => {
    const { key } = e.detail;
    if (!key.startsWith('offer-')) return;
    setOfferUrl(null);
  });

  // Browser-Zurück / Vorwärts
  window.addEventListener('popstate', e => {
    const slug = e.state?.offerSlug ?? getOfferSlugFromUrl();
    if (slug) {
      triggerOverlayOpen(`offer-${slug}`);
    } else {
      const open = document.querySelector('body[class*="overlay-offer-"][class*="-open"]');
      if (open) {
        const match = open.className.match(/overlay-(offer-[\w-]+)-open/);
        if (match) triggerOverlayClose(match[1]);
      }
    }
  });

  // Direktaufruf mit ?beratungsangebot=<slug> → Overlay sofort öffnen
  const initialSlug = config().initialSlug || getOfferSlugFromUrl();
  if (initialSlug) {
    requestAnimationFrame(() => triggerOverlayOpen(`offer-${initialSlug}`));
  }
}

// ── One-Pager Navigation ─────────────────────────────────────────────────────

export function initOnepager() {
  const content  = document.getElementById('onepager-content');
  const navLinks = document.querySelectorAll('.onepager__nav-link');

  // URL-Routing immer initialisieren (auch ohne Nav)
  initOfferUrlRouting();

  // ── Logo-Klick: offenes Overlay schliessen oder nach oben scrollen ─────
  document.querySelectorAll('[data-onepager-home]').forEach(logo => {
    logo.addEventListener('click', () => {
      const openBody = document.body.className.match(/overlay-(offer-[\w-]+)-open/);
      if (openBody) {
        triggerOverlayClose(openBody[1]);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  });

  // ── Mobile-Nav Toggle ───────────────────────────────────────────────────
  document.querySelectorAll('[data-onepager-mobile-nav-toggle]').forEach(btn => {
    btn.addEventListener('click', e => {
      e.preventDefault();
      document.body.classList.toggle('mobile-nav-open');
    });
  });

  // Klick auf einen Nav-Link schliesst die Mobile-Nav
  document.querySelectorAll('.onepager__nav-link').forEach(link => {
    link.addEventListener('click', () => {
      document.body.classList.remove('mobile-nav-open');
    });
  });

  if (!content || !navLinks.length) return;

  // ── Smooth Scroll ───────────────────────────────────────────────────────
  navLinks.forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();
      const targetId = link.getAttribute('href')?.slice(1);
      const target   = targetId ? document.getElementById(targetId) : null;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  // ── Aktiver Link via IntersectionObserver + Slide-Indikator ─────────────
  const anchors   = document.querySelectorAll('.block-anchorlink[id]');
  const indicator = document.querySelector('.onepager__nav-indicator');
  if (!anchors.length) return;

  /** Indicator unter den aktiven Link verschieben */
  const moveIndicator = link => {
    if (!indicator || !link) return;
    const linkRect = link.getBoundingClientRect();
    const navRect  = link.closest('ul').getBoundingClientRect();
    const offsetX  = linkRect.left - navRect.left + linkRect.width / 2 - indicator.offsetWidth / 2;

    indicator.style.transform = `translateX(${offsetX}px)`;
  };

  /** Indicator ein-/ausblenden je nach Scroll-Position */
  const updateIndicatorVisibility = () => {
    if (!indicator) return;
    const firstAnchor = anchors[0];
    if (!firstAnchor) return;
    // Sichtbar sobald der erste Anker sich der Nav-Unterkante nähert.
    // Threshold = Höhe der fixed Nav (+ kleiner Puffer), damit der Indikator
    // bereits beim Scroll-To zum ersten Anker eingeblendet ist.
    const navEl     = document.querySelector('.onepager__nav');
    const threshold = (navEl?.getBoundingClientRect().bottom ?? 0) + 4;
    const reached   = firstAnchor.getBoundingClientRect().top <= threshold;
    indicator.classList.toggle('is-visible', reached);
  };

  const setActive = id => {
    let activeLink = null;
    navLinks.forEach(link => {
      const isActive = link.getAttribute('href') === `#${id}`;
      link.classList.toggle('is-active', isActive);
      if (isActive) activeLink = link;
    });
    moveIndicator(activeLink);
  };

  if (anchors[0]?.id) setActive(anchors[0].id);
  updateIndicatorVisibility();

  // Indicator bei Resize neu positionieren
  window.addEventListener('resize', () => {
    const active = document.querySelector('.onepager__nav-link.is-active');
    moveIndicator(active);
    updateIndicatorVisibility();
  });

  // Sichtbarkeit beim Scrollen aktualisieren
  window.addEventListener('scroll', updateIndicatorVisibility, { passive: true });

  const observer = new IntersectionObserver(
    entries => {
      const visible = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length) setActive(visible[0].target.id);
    },
    {
      root:       null, // Viewport-Scroll
      threshold:  0,
      rootMargin: '0px 0px -80% 0px',
    }
  );

  anchors.forEach(a => observer.observe(a));
}
